import { chatApi } from './chatApi';
import { companionService } from './companionService';
import { cacheService } from './cacheService';
import { imagePreloadService } from './imagePreloadService';

class PreloadService {
  private preloadPromises = new Map<string, Promise<any>>();

  // Preload chat data when user hovers over conversation links
  async preloadChatData(chatId: string): Promise<void> {
    const key = `chat_${chatId}`;
    
    if (this.preloadPromises.has(key)) {
      return this.preloadPromises.get(key);
    }

    const promise = this.performChatPreload(chatId);
    this.preloadPromises.set(key, promise);
    
    // Clean up after 30 seconds
    setTimeout(() => {
      this.preloadPromises.delete(key);
    }, 30000);

    return promise;
  }

  private async performChatPreload(chatId: string): Promise<void> {
    try {
      // Check if already cached
      if (cacheService.getChatMessages(chatId)) {
        return;
      }

      console.log('🔄 Preloading chat data for:', chatId);
      await chatApi.getChatMessages(chatId, true);
      console.log('✅ Preloaded chat data for:', chatId);
    } catch (error) {
      console.warn('Failed to preload chat data:', error);
    }
  }

  // Preload companion data
  async preloadCompanionData(): Promise<void> {
    const key = 'companions';
    
    if (this.preloadPromises.has(key)) {
      return this.preloadPromises.get(key);
    }

    const promise = this.performCompanionPreload();
    this.preloadPromises.set(key, promise);

    return promise;
  }

  private async performCompanionPreload(): Promise<void> {
    try {
      // Check if already cached
      const cachedCompanions = cacheService.getCompanions();
      if (cachedCompanions) {
        // Even if cached, preload images for better UX
        imagePreloadService.preloadCompanionsImages(cachedCompanions.companions || []);
        return;
      }

      console.log('🔄 Preloading companion data');
      const companionData = await companionService.getAllCompanions(true);
      console.log('✅ Preloaded companion data');

      // Preload companion images in background
      if (companionData.companions) {
        imagePreloadService.preloadCompanionsImages(companionData.companions);
      }
    } catch (error) {
      console.warn('Failed to preload companion data:', error);
    }
  }

  // Extract chat ID from URL and preload if present
  preloadFromUrl(url: string): void {
    const urlObj = new URL(url, window.location.origin);
    const chatParam = urlObj.searchParams.get('chat');
    
    if (chatParam) {
      // Preload in background without blocking
      this.preloadChatData(chatParam).catch(() => {
        // Ignore preload errors
      });
    }

    // Always preload companion data for conversation pages
    if (urlObj.pathname.includes('/conversations/')) {
      this.preloadCompanionData().catch(() => {
        // Ignore preload errors
      });
    }
  }

  // Preload user's chat history on login
  async preloadUserChatHistory(): Promise<void> {
    const key = 'user_chat_history';
    
    if (this.preloadPromises.has(key)) {
      return this.preloadPromises.get(key);
    }

    const promise = this.performUserChatHistoryPreload();
    this.preloadPromises.set(key, promise);

    return promise;
  }

  private async performUserChatHistoryPreload(): Promise<void> {
    try {
      console.log('🔄 Preloading user chat history...');
      
      // Preload chat summaries first
      const chatSummary = await chatApi.getChatsSummary();
      console.log('✅ Preloaded chat summaries:', chatSummary.chats.length);

      // Preload the most recent 5 chat conversations for instant access
      const recentChats = chatSummary.chats
        .sort((a, b) => new Date(b.latest_message_timestamp || '').getTime() - new Date(a.latest_message_timestamp || '').getTime())
        .slice(0, 5);

      // Preload chat messages for recent conversations in parallel
      const preloadPromises = recentChats.map(async (chat) => {
        try {
          await chatApi.getChatMessages(chat.chat_id, true);
          console.log('✅ Preloaded messages for chat:', chat.chat_id);
        } catch (error) {
          console.warn('Failed to preload chat:', chat.chat_id, error);
        }
      });

      await Promise.allSettled(preloadPromises);
      console.log('✅ User chat history preloading complete');
    } catch (error) {
      console.warn('Failed to preload user chat history:', error);
    }
  }

  // Preload everything a user might need after login
  async preloadUserData(): Promise<void> {
    const key = 'user_data';
    
    if (this.preloadPromises.has(key)) {
      return this.preloadPromises.get(key);
    }

    const promise = this.performUserDataPreload();
    this.preloadPromises.set(key, promise);

    return promise;
  }

  private async performUserDataPreload(): Promise<void> {
    try {
      console.log('🔄 Preloading user data...');
      
      // Run all preloading in parallel for maximum speed
      await Promise.allSettled([
        this.preloadCompanionData(),
        this.preloadUserChatHistory(),
      ]);

      console.log('✅ User data preloading complete');
    } catch (error) {
      console.warn('Failed to preload user data:', error);
    }
  }

  // Preload images for a specific companion (used when hovering)
  async preloadCompanionImages(companionId: string): Promise<void> {
    const key = `companion_images_${companionId}`;
    
    if (this.preloadPromises.has(key)) {
      return this.preloadPromises.get(key);
    }

    const promise = this.performCompanionImagePreload(companionId);
    this.preloadPromises.set(key, promise);

    return promise;
  }

  private async performCompanionImagePreload(companionId: string): Promise<void> {
    try {
      // Get companion data from cache or API
      const cachedCompanions = cacheService.getCompanions();
      let companion = null;

      if (cachedCompanions?.companions) {
        companion = cachedCompanions.companions.find((c: any) => c.companion_id === companionId);
      }

      if (!companion) {
        // Fetch companion data if not cached
        const companionData = await companionService.getAllCompanions(true);
        companion = companionData.companions.find((c: any) => c.companion_id === companionId);
      }

      if (companion) {
        await imagePreloadService.preloadCompanionImages(companion);
      }
    } catch (error) {
      console.warn('Failed to preload companion images:', error);
    }
  }

  // Clear all preload promises
  clear(): void {
    this.preloadPromises.clear();
    imagePreloadService.clear();
  }
}

export const preloadService = new PreloadService();